import { QRCodeSVG } from 'qrcode.react';
import React, { FC, useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { AccessDenied, PermissionBoundary } from '../../../components';
import { UserSelector } from '../../../components/UserSelector';
import { useTranslation } from '../../../translations';
import { useUserSafe } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';

export const AuthorizeDevice: FC = () => {
  const user = useUserSafe();
  const [userId, setUserId] = useState(user.id);
  const [jwtInfo, setJwtInfo] = useState<{ jwt: string; expires: Date } | null>(null);

  const resetAuth = useCallback(() => {
    setJwtInfo(null);
    setUserId(user.id);
  }, [user.id]);

  const requestAuth = useCallback(async () => {
    try {
      const { jwt, expires } = await api.authorizeDevice(
        'quikticket',
        userId === user.id ? undefined : userId,
      );

      setJwtInfo({
        jwt,
        expires,
      });
    } catch (error) {
      captureError(error as Error);
    }
  }, [user.id, userId]);

  const { ts } = useTranslation();

  return (
    <PermissionBoundary
      inline
      inlineError={<AccessDenied message={ts('quikticket.no_permissions')} />}
      matchAny
      requiredPermissions={['admin:ticket:scan', 'admin:ticket:read', 'system:devices:manage']}
    >
      {jwtInfo ? (
        <div>
          <h6>{ts('authorization_code')}</h6>
          <p>
            <small>{ts('scan_the_qr_code_below', jwtInfo.expires.toLocaleString())}</small>
          </p>
          <div className="text-center">
            <QRCodeSVG value={jwtInfo.jwt} />
          </div>
          <hr />
          <Button block color="secondary" onClick={resetAuth} outline>
            {ts('request_new_code')}
          </Button>
        </div>
      ) : (
        <div>
          <PermissionBoundary inline requiredPermissions="system:devices:manage">
            <h6>{ts('admin_authorize_for_user')}</h6>
            <p>
              <small>{ts('to_authorize_a_device_for_another_user')}</small>
            </p>
            <UserSelector
              id="customRecipients"
              maxItems={1}
              selectionIdsChanged={(ids) => {
                setUserId(ids[0] ?? 0);
              }}
            />
            <hr />
          </PermissionBoundary>
          <Button block color="primary" onClick={requestAuth}>
            {ts('request_code')}
          </Button>
        </div>
      )}
    </PermissionBoundary>
  );
};
